import {
  Alert,
  Box,
  Card,
  Flex,
  Heading,
  HStack,
  Text,
  VStack,
  Separator,
  Button,
  useMediaQuery,
} from '@chakra-ui/react'
import React from 'react'
import { useMemo, useState, useEffect, useRef } from 'react'
import { LuPaperclip, LuTextSelect, LuFile, LuX } from 'react-icons/lu'
import { WidgetShell } from '@/components/widget-wrappers/WidgetShell'
import { TabbedSplitLayout } from '@/components/widget-wrappers/TabbedSplitLayout'
import { EmptyStateCard } from '@/components/widget-wrappers/EmptyStateCard'
import { MobileDrawer } from '@/components/widget-wrappers/MobileDrawer'
import SkeletonCard from './NotificationCardSkeleton'
import { NotificationCard } from './NotificationCard'
import {
  getNotifications,
  type NotificationDTO,
  markNotificationAsRead,
  deleteNotification,
} from '@/api/notifications.api'
import type { OrderSummary } from '@/components/order/OrderWidget'
import { mapAttachmentToFrontend } from '@/helper/order.helper'
import type { OrderAttachmentFileDetails } from '@/common/order.common'

import { listOrderAttachments } from '@/api/order-attachments.api'

type NotificationItem = {
  id: string
  title: string
  message: string
  dateLabel: string
  isRead: boolean
  orderId?: number
  attachmentsCount?: number
}

type TabKey = 'all' | 'unread' | 'read'

function NotificationsPanel({
  orderById,
  focusNotificationId,
  onFocusHandled,
  refreshKey,
}: {
  orderById?: Record<number, OrderSummary>
  focusNotificationId?: string | null
  onFocusHandled?: () => void
  refreshKey?: number
}) {
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        const res = await getNotifications({ status: 'all' })
        if (!alive) return
        const mapped = res.map(toUiItem)
        setItems(mapped)

        // if selected notification disappeared (e.g. deleted), clear right panel
        if (selectedId && !mapped.some(n => n.id === selectedId)) {
          setSelectedId(null)
        }
      } catch (e) {
        console.error('Failed to load notifications:', e)
        // keep empty; UI will show "Keine Treffer" for filters
      } finally {
        if (alive) setLoading(false)
      }
    })()

    return () => {
      alive = false
    }
  }, [refreshKey])

  type AttachmentItem = {
    id: number
    fileName: string
    sizeBytes?: number
  }

  const [attachments, setAttachments] = useState<AttachmentItem[]>([])
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)

  const [items, setItems] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState(false)
  const prevSelectedIdRef = useRef<string | null>(null)
  const formatBytes = (bytes?: number) => {
    if (bytes == null) return ''
    if (bytes < 1024) return `${bytes} B`

    const kb = bytes / 1024
    if (kb < 1024) return `${Math.round(kb)} KB`

    const mb = kb / 1024
    return `${mb.toFixed(1)} MB`
  }

  const formatDate = (iso?: string) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d)
  }

  const toUiItem = (dto: NotificationDTO): NotificationItem => {
    const created = dto.createdAt ?? dto.date

    return {
      id: String(dto.id),
      title: dto.title ?? 'Benachrichtigung',
      message: dto.message ?? '',
      dateLabel: formatDate(created),
      isRead: dto.read ?? !!dto.readAt,
      orderId: (dto as any).orderId,
    }
  }

  const [tab, setTab] = useState<TabKey>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const hasAnyNotifications = items.length > 0

  const counts = useMemo(() => {
    const unread = items.filter(i => !i.isRead).length
    const read = items.filter(i => i.isRead).length
    return { all: items.length, unread, read }
  }, [items])

  const filtered = useMemo(() => {
    let base =
      tab === 'unread'
        ? items.filter(i => !i.isRead)
        : tab === 'read'
          ? items.filter(i => i.isRead)
          : items

    // keep selected visible even if it no longer matches the filter
    if (selectedId && !base.some(i => i.id === selectedId)) {
      const sel = items.find(i => i.id === selectedId)
      if (sel) base = [sel, ...base]
    }

    return base
  }, [items, tab, selectedId])

  const selected = useMemo(() => items.find(i => i.id === selectedId) ?? null, [items, selectedId])

  useEffect(() => {
    let alive = true

    ;(async () => {
      if (!selected?.orderId) {
        setAttachments([])
        return
      }

      try {
        setAttachmentsLoading(true)

        const res = await listOrderAttachments(selected.orderId)

        if (!alive) return

        const items = (res as any).attachments.map((a: any) => ({
          id: Number(a.id),
          fileName: a.filename,
          sizeBytes: Number(a.fileSize),
        }))

        setAttachments(items)
      } catch (e) {
        console.error('Failed to load attachments', e)
        if (alive) setAttachments([])
      } finally {
        if (alive) setAttachmentsLoading(false)
      }
    })()

    return () => {
      alive = false
    }
  }, [selected?.orderId])

  useEffect(() => {
    const prevId = prevSelectedIdRef.current
    const currentId = selectedId

    if (prevId && prevId !== currentId) {
      const prevItem = items.find(n => n.id === prevId)
      if (prevItem && !prevItem.isRead) {
        setItems(prev => prev.map(n => (n.id === prevId ? { ...n, isRead: true } : n)))

        markNotificationAsRead(Number(prevId)).catch(e => {
          console.error('Failed to mark notification as read', e)
          setItems(prev => prev.map(n => (n.id === prevId ? { ...n, isRead: false } : n)))
        })
      }
    }

    prevSelectedIdRef.current = currentId
  }, [selectedId, items])

  useEffect(() => {
    const currentId = selectedId
    if (!currentId) return

    const item = items.find(n => n.id === currentId)
    if (!item || item.isRead) return

    setItems(prev => prev.map(n => (n.id === currentId ? { ...n, isRead: true } : n)))

    markNotificationAsRead(Number(currentId)).catch(e => {
      console.error('Failed to mark notification as read on tab switch', e)
      setItems(prev => prev.map(n => (n.id === currentId ? { ...n, isRead: false } : n)))
    })
  }, [tab])

  useEffect(() => {
    if (!focusNotificationId) return

    setTab('unread')
    setSelectedId(String(focusNotificationId))

    requestAnimationFrame(() => {
      const el = document.getElementById(`notif-row-${focusNotificationId}`)
      el?.scrollIntoView({ block: 'nearest' })
    })

    onFocusHandled?.()
  }, [focusNotificationId, onFocusHandled])

  const attachmentsCountFor = (n: NotificationItem) => {
    if (!n.orderId || !orderById) return 0
    const o = orderById[n.orderId]
    return o?.attachmentCount ?? 0
  }

  const [isMobile] = useMediaQuery(['(max-width: 767px)'])
  const [isDrawerOpen, setDrawerOpen] = useState(false)

  const onSelect = (id: string) => {
    setSelectedId(id)
    if (isMobile) setDrawerOpen(true)
  }

  const deleteButton = selected ? (
    <Button
      variant="ghost"
      onClick={async () => {
        const cur = selected
        if (!cur) return

        const id = Number(cur.id)
        setItems(prev => prev.filter(n => n.id !== cur.id))
        setSelectedId(null)

        try {
          await deleteNotification(id)
          if (isMobile) setDrawerOpen(false)
        } catch (e) {
          console.error('Failed to delete notification', e)
          setItems(prev => [...prev, cur])
        }
      }}
    >
      <HStack gap={2} justify="center" w="full">
        <LuX />
        <Text>Benachrichtigung löschen</Text>
      </HStack>
    </Button>
  ) : null

  const rightDetailsContent = !selected ? (
    <EmptyStateCard
      icon={<LuTextSelect size={36} />}
      title={
        hasAnyNotifications ? 'Keine Benachrichtigung ausgewählt' : 'Keine Benachrichtigungen'
      }
      description={
        hasAnyNotifications
          ? 'Wählen Sie eine Benachrichtigung aus der Liste aus.'
          : 'Aktuell gibt es keine Benachrichtigungen.'
      }
    />
  ) : (
    <Flex
      direction="column"
      h="full"
      p={1}
      justifyContent={{ base: 'flex-start', md: 'space-between' }}
    >
      {/* Header */}
      <Flex align="center" justify="space-between" gap={4} wrap="nowrap">
        <Heading size="xl">{selected.title}</Heading>

        <HStack gap={4} color="fg.muted" flexShrink={0}>
          <HStack gap="1" align="center">
            <LuPaperclip />
            <Text textStyle="metadata">{attachments.length}</Text>
          </HStack>
          <Text textStyle="metadata">{selected.dateLabel}</Text>
        </HStack>
      </Flex>

      <Separator my={{ base: 2, md: 4 }} />

      {/* Message */}
      <Text mb={{ base: 2, md: 6 }}>{selected.message}</Text>

      {/* Attachments */}
      <Heading size="xl" mb={3} color="fg.muted">
        Anhänge
      </Heading>

      {attachmentsLoading ? (
        <Alert.Root status="info" variant="subtle" mb={{ md: 6 }}>
          <Alert.Indicator />
          <Alert.Description>Anhänge werden geladen…</Alert.Description>
        </Alert.Root>
      ) : attachments.length === 0 ? (
        <Alert.Root status="neutral" variant="subtle" mb={{ md: 6 }}>
          <Alert.Indicator />
          <Alert.Description>Keine Anhänge vorhanden.</Alert.Description>
        </Alert.Root>
      ) : (
        <VStack align="stretch" gap={3} mb={6}>
          {attachments.map(a => (
            <Card.Root key={a.id}>
              <Card.Body>
                <HStack gap={5}>
                  <LuFile />
                  <Box>
                    <Card.Title>{a.fileName}</Card.Title>
                    <Card.Description>{formatBytes(a.sizeBytes)}</Card.Description>
                  </Box>
                </HStack>
              </Card.Body>
            </Card.Root>
          ))}
        </VStack>
      )}

      {/* Delete Button */}
      {!isMobile && deleteButton}
    </Flex>
  )

  const leftDetailsContent = loading ? (
    <VStack align="stretch" gap={3}>
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </VStack>
  ) : (
    <VStack align="stretch" gap={3}>
      {filtered.map(n => (
        <Box key={n.id} id={`notif-row-${n.id}`}>
          <NotificationCard
            title={n.title}
            message={n.message}
            dateLabel={n.dateLabel}
            isRead={n.isRead}
            attachmentsCount={attachmentsCountFor(n)}
            selected={n.id === selectedId}
            onClick={() => onSelect(n.id)}
          />
        </Box>
      ))}

      {filtered.length === 0 && (
        <EmptyStateCard
          title="Keine Treffer"
          description="Für diesen Filter gibt es aktuell keine Benachrichtigungen."
        />
      )}
    </VStack>
  )

  const rightDesktopNode = (
    <Card.Root flex="1" borderColor="bg.muted" minH={0} overflowY="auto">
      <Card.Body>{rightDetailsContent}</Card.Body>
    </Card.Root>
  )
  const leftDesktopNode = (
    <Box
      flex="1"
      pr={1}
      minH={0}
      overflowY="auto"
      css={{
        scrollbarGutter: 'stable',
        '@media (max-width: 767px)': {
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '999px',
        },
      }}
    >
      {leftDetailsContent}
    </Box>
  )

  const rightDrawerNode = <Flex h="full">{rightDetailsContent}</Flex>

  return (
    <WidgetShell
      id="dashboard-notifications"
      data-testid="notifications-panel"
      title="Benachrichtigungen"
    >
      <TabbedSplitLayout<TabKey>
        tab={tab}
        onTabChange={t => {
          setTab(t)
          setSelectedId(null)
          if (isMobile) setDrawerOpen(false)
        }}
        tabs={[
          { key: 'all', label: 'Alle', count: counts.all },
          { key: 'unread', label: 'Ungelesen', count: counts.unread },
          { key: 'read', label: 'Gelesen', count: counts.read },
        ]}
        hideLeft={!hasAnyNotifications}
        left={leftDesktopNode}
        right={isMobile && hasAnyNotifications ? null : rightDesktopNode}
      />

      {/* Mobile Drawer */}
      {isMobile && (
        <MobileDrawer
          open={isDrawerOpen}
          onClose={() => setDrawerOpen(false)}
          variant={attachments.length > 1 ? 'full' : 'half'}
          header={
            <Box>
              <Heading size="xl">Benachrichtigung</Heading>
            </Box>
          }
          footer={deleteButton}
        >
          {rightDrawerNode}
        </MobileDrawer>
      )}
    </WidgetShell>
  )
}
export default React.memo(NotificationsPanel)
